import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { of } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { UsersService } from 'src/app/services/users.service';
import { AuthenticationService } from '../../services/authentication.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MatCard } from '@angular/material/card';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    imports: [
        MatCard,
        ReactiveFormsModule,
        FaIconComponent,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatIconButton,
        MatSuffix,
        MatIcon,
        MatButton,
        TranslateModule,
        RouterModule,
    ],
})
export class LoginComponent implements OnInit {
    loginForm: UntypedFormGroup;
    hide = true;
    authenticationInvalid = false;
    version: string = environment.version;

    // Icône clé
    public fakey = faKey;

    // Délai de prévenance de l'expiration de mot de passe en jours
    private delayNotice: number = 15;

    constructor(
        private router: Router,
        private formBuilder: UntypedFormBuilder,
        private authenticationService: AuthenticationService,
        private userService: UsersService,
        private organizationsService: OrganizationsService,
        private spinner: NgxSpinnerService,
        private toastrService: ToastrService,
        private translate: TranslateService
    ) {}

    ngOnInit() {
        this.loginForm = this.formBuilder.group({
            username: ['', Validators.required],
            password: ['', Validators.required],
        });

        if (this.userService.getSessionCurrentUser() != null) {
            this.router.navigate(['./profile']);
        }

        this.spinner.hide();
    }

    onSubmit() {
        this.spinner.show();

        this.authenticationService
            .login(this.loginForm.get('username').value, this.loginForm.get('password').value)
            .pipe(
                mergeMap((isAuthentified) =>
                    isAuthentified
                        ? // Si authentifié
                          this.userService.getCurrentUser().pipe(
                              switchMap((user) => {
                                  return this.organizationsService.getOrganisation(user.organizationId).pipe(
                                      map((organization) => {
                                          this.checkPasswordExpiration();
                                          this.organizationsService.setCurrentOrganization(organization);
                                          return true;
                                      })
                                  );
                              })
                          )
                        : // Si non authentifié
                          of(false)
                )
            )
            .subscribe({
                next: (isAuthentified) => {
                    this.authenticationInvalid = !isAuthentified;

                    if (isAuthentified === true) {
                        this.router.navigate(['/profile']);
                    }

                    this.spinner.hide();
                },
                error: () => {
                    this.authenticationInvalid = true;
                    this.spinner.hide();
                },
            });
    }

    private checkPasswordExpiration() {
        const passwordExpireOn = sessionStorage.getItem('passwordExpireOn');
        sessionStorage.removeItem('passwordExpireOn');

        if (passwordExpireOn) {
            const passwordExpireDate = new Date(passwordExpireOn);

            if (passwordExpireDate.getFullYear() > 1) {
                let limiteDate = new Date(passwordExpireOn);
                limiteDate.setDate(passwordExpireDate.getDate() - this.delayNotice);

                const today = new Date();
                // today.setDate(today.getDate() + 140);

                if (today > limiteDate) {
                    this.toastrService.info(this.translate.instant('Login.MessagePasswordExpire', { days: this.DaysBetweenTwoDate(today, passwordExpireDate) + 1 }), '', {
                        enableHtml: true,
                    });
                }
            }
        }
    }

    DaysBetweenTwoDate(dateDebut: Date, dateFin: Date): number {
        const uneJourneeEnMillisecondes = 24 * 60 * 60 * 1000; // Nombre de millisecondes dans une journée
        const differenceEnMillisecondes = dateFin.getTime() - dateDebut.getTime();
        const nombreDeJours = Math.floor(differenceEnMillisecondes / uneJourneeEnMillisecondes);
        return nombreDeJours;
    }
}
